import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import memoize from 'lodash/memoize';
import { type CardEntryGroup, isCardEntryGroup } from '../../../model/types.tsx';
import {
	getParentIdByChildIdSelector as makeGetParentIdByChildIdSelector,
	makeGetCardListIdByCardId,
	makeGetOrderedCardEntries,
} from '../cards/index.tsx';

export const makeGetCardGroupByParentId = createSelector(
	[makeGetOrderedCardEntries],
	(getOrderedCardEntries) =>
		memoize(
			(cardListId: string, parentId: number) => {
				const cardEntries = getOrderedCardEntries(cardListId);

				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				return cardEntries.find(
					(cardEntry) => isCardEntryGroup(cardEntry) && cardEntry.parentId === parentId,
				) as CardEntryGroup | undefined;
			},
			(cardListId, parentId) => `${cardListId}::${parentId}`,
		),
);

export const makeGetCardGroupByChildId = createSelector(
	[makeGetCardListIdByCardId, makeGetParentIdByChildIdSelector, makeGetCardGroupByParentId],
	(getCardListIdByCardId, getParentIdByChildId, getCardGroupByParentId) => {
		const getCardGroupByChildId = (cardId: number) => {
			const cardListId = getCardListIdByCardId(cardId);

			if (!cardListId) {
				return null;
			}

			const parentId = getParentIdByChildId(cardId);
			return parentId ? getCardGroupByParentId(cardListId, parentId) : null;
		};

		return memoize(getCardGroupByChildId);
	},
);

export const makeGetBottomCardIdFromCardGroup = createSelector(
	[makeGetCardGroupByParentId],
	(getCardGroupByParentId) => (cardListId: string, parentId: number, ignoredIds: number[]) => {
		const cardGroup = getCardGroupByParentId(cardListId, parentId);

		if (!cardGroup) {
			return null;
		}

		const list = difference(cardGroup.cardIds, ignoredIds);
		return list.length > 0 ? list[list.length - 1] : null;
	},
);

export const makeIsCardInCollapsedCardGroup = createSelector(
	[makeGetCardGroupByChildId],
	(getCardGroupByChildId) =>
		memoize((cardId: number) => {
			const cardGroup = getCardGroupByChildId(cardId);

			if (cardGroup) {
				return cardGroup.isCollapsed;
			}

			return false;
		}),
);
